import React, { useEffect, useState } from 'react';
import { Command } from 'cmdk';
import { useNavigate } from 'react-router-dom';
import { useTheme } from 'next-themes';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import {
  LayoutDashboard, Dumbbell, Package, CalendarDays, Users,
  Activity, Building, ClipboardList, Sun, Moon, Monitor, LogOut, Search, Bookmark, Radio, Coins, UserPlus,
} from 'lucide-react';

const pages = [
  { label: 'Dashboard', href: '/', icon: LayoutDashboard, group: 'Pages' },
  { label: 'Live Studio', href: '/live-studio', icon: Radio, group: 'Pages' },
  { label: 'Exercises', href: '/exercises', icon: Dumbbell, group: 'Pages' },
  { label: 'Advanced Search', href: '/exercises/search', icon: Search, group: 'Pages' },
  { label: 'Bookmarks', href: '/bookmarks', icon: Bookmark, group: 'Pages' },
  { label: 'Programs', href: '/programs', icon: Package, group: 'Pages' },
  { label: 'Sessions', href: '/sessions', icon: CalendarDays, group: 'Pages' },
  { label: 'Session Builder', href: '/session-builder', icon: ClipboardList, group: 'Pages' },
  { label: 'New Subscribers', href: '/new-subscribers', icon: UserPlus, group: 'Pages' },
  { label: 'User Programs', href: '/user-program', icon: Users, group: 'Pages' },
  { label: 'User Activity', href: '/user-activity', icon: Activity, group: 'Pages' },
  { label: 'Token Usage', href: '/token-usage', icon: Coins, group: 'Pages' },
  { label: 'B2B', href: '/b2b', icon: Building, group: 'Pages' },
];

export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const { signOut, canAccessRoute } = useAuth();
  const visiblePages = pages.filter((page) => page.href === '/' || canAccessRoute(page.href));

  useEffect(() => {
    const down = (e) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  const runCommand = (fn) => {
    setOpen(false);
    fn();
  };

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/50" onClick={() => setOpen(false)} />
      <div className="fixed left-1/2 top-[20%] z-[51] w-full max-w-lg -translate-x-1/2">
        <Command
          className="rounded-lg border bg-popover text-popover-foreground shadow-2xl overflow-hidden"
          loop
        >
          <div className="flex items-center border-b px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
            <Command.Input
              placeholder="Type a command or search..."
              className="flex h-11 w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
              autoFocus
            />
          </div>
          <Command.List className="max-h-[300px] overflow-y-auto p-1">
            <Command.Empty className="py-6 text-center text-sm text-muted-foreground">
              No results found.
            </Command.Empty>

            <Command.Group heading="Pages" className="px-1 py-1.5 text-xs font-medium text-muted-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground">
              {visiblePages.map((page) => {
                const Icon = page.icon;
                return (
                  <Command.Item
                    key={page.href}
                    value={page.label}
                    onSelect={() => runCommand(() => navigate(page.href))}
                    className="flex items-center gap-2 rounded-md px-2 py-2 text-sm cursor-pointer aria-selected:bg-accent aria-selected:text-accent-foreground"
                  >
                    <Icon className="h-4 w-4 text-muted-foreground" />
                    {page.label}
                  </Command.Item>
                );
              })}
            </Command.Group>

            <Command.Separator className="my-1 h-px bg-border" />

            <Command.Group heading="Actions" className="px-1 py-1.5 text-xs font-medium text-muted-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground">
              <Command.Item
                value="Light mode"
                onSelect={() => runCommand(() => setTheme('light'))}
                className="flex items-center gap-2 rounded-md px-2 py-2 text-sm cursor-pointer aria-selected:bg-accent aria-selected:text-accent-foreground"
              >
                <Sun className="h-4 w-4 text-muted-foreground" />
                Light Mode
              </Command.Item>
              <Command.Item
                value="Dark mode"
                onSelect={() => runCommand(() => setTheme('dark'))}
                className="flex items-center gap-2 rounded-md px-2 py-2 text-sm cursor-pointer aria-selected:bg-accent aria-selected:text-accent-foreground"
              >
                <Moon className="h-4 w-4 text-muted-foreground" />
                Dark Mode
              </Command.Item>
              <Command.Item
                value="System theme"
                onSelect={() => runCommand(() => setTheme('system'))}
                className="flex items-center gap-2 rounded-md px-2 py-2 text-sm cursor-pointer aria-selected:bg-accent aria-selected:text-accent-foreground"
              >
                <Monitor className="h-4 w-4 text-muted-foreground" />
                System Theme
              </Command.Item>
              <Command.Item
                value="Sign out"
                onSelect={() => runCommand(signOut)}
                className="flex items-center gap-2 rounded-md px-2 py-2 text-sm cursor-pointer aria-selected:bg-accent aria-selected:text-accent-foreground"
              >
                <LogOut className="h-4 w-4 text-muted-foreground" />
                Sign Out
              </Command.Item>
            </Command.Group>
          </Command.List>
        </Command>
      </div>
    </>
  );
}
